import { supabase } from '@/integrations/supabase/client';

export interface ChatInteractionRecord {
  id: string;
  conversation_id: string | null;
  user_message: string;
  ai_response: string;
  metadata: Record<string, any> | null;
  created_at: string;
}

const TABLE = 'chat_interactions';

export interface SaveInteractionParams {
  userId: string;
  sessionId: string;
  conversationId: string;
  characterType?: string;
  userMessage: string;
  aiResponse: string;
  context?: Record<string, any>;
  metadata?: Record<string, any>;
  responseTimeMs?: number;
}

export const microChatService = {
  async fetchHistory(sessionId: string, characterType = 'lyra') {
    const { data, error } = await supabase
      .from(TABLE)
      .select('id, conversation_id, user_message, ai_response, metadata, created_at')
      .eq('session_id', sessionId)
      .eq('character_type', characterType)
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(`[microChatService] Failed to fetch history: ${error.message}`);
    }

    const history = (data as ChatInteractionRecord[]) || [];
    const latestConversationId = history.length > 0 ? history[history.length - 1].conversation_id : null;

    return {
      conversationId: latestConversationId,
      history
    };
  },

  async saveInteraction({
    userId,
    sessionId,
    conversationId,
    characterType = 'lyra',
    userMessage,
    aiResponse,
    context,
    metadata,
    responseTimeMs
  }: SaveInteractionParams) {
    const payload = {
      user_id: userId,
      session_id: sessionId,
      conversation_id: conversationId,
      character_type: characterType,
      user_message: userMessage,
      ai_response: aiResponse,
      context: context || {},
      metadata: {
        ...(metadata || {}),
        response_time_ms: responseTimeMs ?? null
      },
      response_time_ms: responseTimeMs ?? null
    };

    const { error } = await supabase.from(TABLE).insert(payload);

    if (error) {
      throw new Error(`[microChatService] Failed to save interaction: ${error.message}`);
    }
  },

  async clearSessionHistory(sessionId: string, userId: string, characterType = 'lyra') {
    const { error } = await supabase
      .from(TABLE)
      .delete()
      .eq('session_id', sessionId)
      .eq('user_id', userId)
      .eq('character_type', characterType);

    if (error) {
      throw new Error(`[microChatService] Failed to clear history: ${error.message}`);
    }
  }
};
